/**
 * Related orders (GP-1428) — the shared vocabulary for "this patient already has
 * another order going out on this delivery date".
 *
 * ONE source of truth for the shape, the stage wording and the warning text, used
 * by BOTH surfaces the ticket asks for: the Packaging queue row chip
 * (`orderQueueColumns.createCompactOrderInfoColumn`) and the packaging-screen
 * banner (`common/OrderSiblingBanners.RelatedOrdersBanner`). They must never say
 * different things about the same sibling.
 *
 * Backend contract: `backend-v2/src/orders/utils/related-orders.util.ts`.
 */

export type RelatedOrderRelation = 'split' | 'same_day';

export interface RelatedOrderSummary {
  id: string;
  orderNumber: string;
  workflowStage: string;
  status: string;
  medicationName: string | null;
  rxNumber: string | null;
  /** The bag the existing package is already in, when it has one. */
  bagNumber: string | null;
  deliveryDate: string | null;
  inTroubleshooting: boolean;
  relation: RelatedOrderRelation;
}

/** Full stage wording, for the banner and the tooltip. */
const STAGE_LABELS: Record<string, string> = {
  billing: 'Billing',
  clinic_scheduling: 'Scheduling',
  clinical_verification: 'Clinical Verification',
  packaging: 'Packaging',
  product_verification: 'Product Verification',
  ready_to_ship: 'Ready to Ship',
  staging: 'Staging',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  ready_for_pickup: 'Ready for Pickup',
  picked_up: 'Picked Up',
  delivery_missed: 'Delivery Missed',
  cancelled: 'Cancelled',
};

/**
 * Compact stage wording for the queue chip, where the column is 140px wide.
 * Deliberately the SAME abbreviations Patient 360's Order History already shows
 * (OrdersTable.getStageDisplayName) — "Ready" is what the packer saw one screen
 * away in the bug report, so the chip must not invent a second name for it.
 */
const SHORT_STAGE_LABELS: Record<string, string> = {
  clinic_scheduling: 'Scheduling',
  clinical_verification: 'Clinical',
  product_verification: 'Product',
  ready_to_ship: 'Ready',
  out_for_delivery: 'Out',
  ready_for_pickup: 'Pickup',
};

const titleCase = (stage: string): string =>
  stage
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const formatRelatedOrderStage = (
  stage: string | null | undefined,
  options?: { short?: boolean },
): string => {
  const key = String(stage ?? '').trim();
  if (!key) return 'Unknown';
  if (options?.short && SHORT_STAGE_LABELS[key]) return SHORT_STAGE_LABELS[key];
  return STAGE_LABELS[key] || titleCase(key);
};

/** Where the sibling actually sits, troubleshooting included. */
export const describeRelatedOrderStage = (related: RelatedOrderSummary): string =>
  related.inTroubleshooting
    ? `Troubleshooting (${formatRelatedOrderStage(related.workflowStage)})`
    : formatRelatedOrderStage(related.workflowStage);

/**
 * The chip's label. One sibling names its stage — that is the whole point of the
 * flag ("1 related order — already Ready to Ship"); several fall back to a count
 * and let the tooltip / banner list them.
 */
export const relatedOrdersChipLabel = (related: RelatedOrderSummary[]): string =>
  related.length === 1
    ? `1 related · ${formatRelatedOrderStage(related[0].workflowStage, { short: true })}`
    : `${related.length} related orders`;

/** One line per sibling: order number, drug, stage, and the bag it is already in. */
export const describeRelatedOrder = (related: RelatedOrderSummary): string => {
  const drug = related.medicationName || related.rxNumber || 'medication not recorded';
  const bag = related.bagNumber ? `, bag ${related.bagNumber}` : '';
  return `${related.orderNumber} (${drug}) — ${describeRelatedOrderStage(related)}${bag}`;
};

/**
 * The instruction, not just the fact. The packer's decision is "consolidate into
 * the existing bag" vs "build a second one", so the copy has to say which.
 */
export const RELATED_ORDERS_ACTION_TEXT =
  'Same patient, same delivery date — consolidate into the existing package instead of creating a second one.';

export const relatedOrdersTooltip = (related: RelatedOrderSummary[]): string =>
  [
    related.length === 1
      ? '1 related order for this patient on this delivery date:'
      : `${related.length} related orders for this patient on this delivery date:`,
    ...related.map((r) => `• ${describeRelatedOrder(r)}`),
    RELATED_ORDERS_ACTION_TEXT,
  ].join('\n');

/** Narrow an unknown API/grid value to the summaries the UI can render. */
export const toRelatedOrders = (value: unknown): RelatedOrderSummary[] =>
  Array.isArray(value)
    ? (value.filter(
        (entry: any) => entry && typeof entry.orderNumber === 'string',
      ) as RelatedOrderSummary[])
    : [];
